import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useState } from "react";

import { DailyChart } from "@/components/analytics/DailyChart";
import { KPICards } from "@/components/analytics/KPICards";
import { TopPagesTable } from "@/components/analytics/TopPagesTable";
import { TopQueriesTable } from "@/components/analytics/TopQueriesTable";
import { api } from "@/lib/api";
import { formatRelativeTime } from "@/lib/formatters";
import { isAuthenticated } from "@/lib/pb";

export const Route = createFileRoute("/analytics/")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const [forceRefresh, setForceRefresh] = useState(false);

  const query = useQuery({
    queryKey: ["analytics", forceRefresh],
    queryFn: () => api.analytics.read(forceRefresh),
  });

  if (query.isLoading) {
    return <Shell><Skeleton label="fetching gsc snapshot" /></Shell>;
  }
  if (query.error || !query.data) {
    return (
      <Shell>
        <div className="surface p-6 font-mono text-sm text-danger">
          {(query.error as Error)?.message ?? "no data"}
        </div>
      </Shell>
    );
  }

  const data = query.data;

  return (
    <Shell>
      <Header
        period={data.period}
        generatedAt={data.generated_at}
        stale={data.stale}
        onRefresh={() => setForceRefresh((v) => !v)}
        refreshing={query.isFetching}
      />

      <KPICards summary={data.summary} deltas={data.deltas} />

      <DailyChart rows={data.daily} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <TopQueriesTable rows={data.top_queries} />
        <TopPagesTable rows={data.top_pages} />
      </div>
    </Shell>
  );
}

// ─── layout helpers ──────────────────────────────────────────────────
function Shell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-6xl mx-auto space-y-4">{children}</div>;
}

function Header({
  period,
  generatedAt,
  stale,
  onRefresh,
  refreshing,
}: {
  period: string;
  generatedAt: string;
  stale?: boolean;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-end justify-between"
    >
      <div>
        <p className="path-chip">/analytics · search console</p>
        <div className="flex items-baseline gap-3 mt-1">
          <h1 className="text-3xl font-semibold">Analytics</h1>
          <span className="font-mono text-sm text-muted">[{period}]</span>
        </div>
        <p className="mt-1 font-mono text-xs text-muted/60">
          {stale ? "stale · " : ""}snapshot {formatRelativeTime(generatedAt)}
        </p>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className="px-3 py-1.5 text-xs font-mono border border-border rounded-sm text-muted hover:text-text hover:border-primary/40 transition-colors disabled:opacity-50"
      >
        {refreshing ? "refreshing…" : "$ refresh"}
      </button>
    </motion.div>
  );
}

function Skeleton({ label }: { label: string }) {
  return (
    <div className="surface p-12 text-center">
      <p className="font-mono text-sm text-muted">$ {label}…</p>
    </div>
  );
}
